import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, Loader2, Briefcase, Users, UserCheck, Send,
  CheckCircle2, Gauge, Flag, ShieldAlert, TrendingUp, Mic,
} from "lucide-react";
import FlagRanking from "../components/FlagRanking";

const STATUS_CLS = {
  active:   "bg-[#10B981]/10 text-[#047857] border-[#10B981]/30",
  pending:  "bg-amber-50 text-amber-700 border-amber-300",
  approved: "bg-[#0025D8]/10 text-[#0025D8] border-[#0025D8]/30",
  past_due: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30",
  canceled: "bg-muted text-muted-foreground border-border",
};

const ROLE_STATUS_CLS = {
  active: "bg-[#10B981]/10 text-[#10B981]",
  draft:  "bg-secondary text-muted-foreground",
  paused: "bg-[#F59E0B]/10 text-[#F59E0B]",
  closed: "bg-[#E11D48]/10 text-[#E11D48]",
};

const fmtNum = (n) => (n == null ? "—" : n);
const fmtScore = (n) => (n == null ? "—" : `${n}`);

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`font-display font-bold text-2xl ${tone || "text-[#0F172A]"}`}>{value}</div>
    </div>
  );
}

export default function AdminCompanyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/companies/${id}/analytics`);
        setData(data);
      } catch {
        toast.error("Could not load company analytics");
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-20 flex items-center justify-center text-muted-foreground font-mono text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading company analytics…
      </div>
    );
  }
  if (!data) return null;

  const { company, totals, roles, monthly_sent = [], sent_this_month, sent_last_month, sent_all_time, flag_ranking = [] } = data;
  const statusCls = STATUS_CLS[company.company_status] || STATUS_CLS.pending;

  const maxMonth = Math.max(1, ...monthly_sent.map((m) => m.count));
  const ivLimit = company.interview_limit;               // null = unlimited
  const ivUsed = company.interviews_used_cycle || 0;
  const totalSent = company.total_invites_sent ?? sent_all_time ?? 0;
  const cycleOver = ivLimit != null && ivUsed >= ivLimit;
  const cyclePct = ivLimit ? Math.min(100, Math.round((ivUsed / ivLimit) * 100)) : 0;
  const momDelta = (sent_this_month || 0) - (sent_last_month || 0);
  const monLabel = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleString("en", { month: "short" });

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/admin/companies" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-[#0025D8] mb-6">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to companies
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="font-display font-black text-3xl tracking-tight text-[#0F172A]">{company.name}</h1>
            <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border ${statusCls}`}>
              {company.company_status || "pending"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground font-mono">
            {company.email}{company.plan_label ? ` · ${company.plan_label} plan` : ""}
          </p>
        </div>
      </div>

      {/* Aggregate note */}
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground bg-secondary/40 border border-border rounded-lg px-3 py-2 mb-5">
        <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" />
        Aggregate figures only — no individual candidate names, emails, or scores are shown, for candidate privacy.
      </div>

      {/* Company totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard icon={Briefcase} label="Roles" value={fmtNum(totals.roles)} />
        <StatCard icon={Users} label="Applied" value={fmtNum(totals.applied)} />
        <StatCard icon={UserCheck} label="Shortlisted" value={fmtNum(totals.shortlisted)} />
        <StatCard icon={Send} label="Invited" value={fmtNum(totals.invited)} />
        <StatCard icon={CheckCircle2} label="Completed" value={fmtNum(totals.completed)} />
        <StatCard icon={Gauge} label="Avg score" value={fmtScore(totals.avg_score)} tone="text-[#0025D8]" />
        <StatCard icon={Flag} label="Avg flags / interview" value={fmtScore(totals.avg_flags)}
          tone={totals.avg_flags > 0 ? "text-[#E11D48]" : "text-[#0F172A]"} />
      </div>

      {/* Interview volume — upsell intelligence */}
      <div className="rounded-xl border border-border bg-white p-4 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <TrendingUp className="w-3.5 h-3.5" /> Interviews sent · last 12 months
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span><span className="text-muted-foreground text-xs">This month </span><span className="font-mono font-bold">{sent_this_month ?? 0}</span></span>
            <span><span className="text-muted-foreground text-xs">Last month </span><span className="font-mono font-bold">{sent_last_month ?? 0}</span></span>
            {momDelta !== 0 && (
              <span className={`font-mono text-xs font-semibold ${momDelta > 0 ? "text-[#10B981]" : "text-[#E11D48]"}`}>
                {momDelta > 0 ? "▲" : "▼"} {Math.abs(momDelta)}
              </span>
            )}
            <span title="Lifetime counter — never resets, includes allowance + credit sends"><span className="text-muted-foreground text-xs">All-time </span><span className="font-mono font-bold">{totalSent}</span></span>
          </div>
        </div>

        {/* Cycle usage vs plan limit (the upsell signal) */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="flex items-center gap-1.5 text-muted-foreground"><Mic className="w-3 h-3" /> This billing cycle</span>
            <span className={`font-mono font-semibold ${cycleOver ? "text-[#E11D48]" : "text-foreground"}`}>
              {ivLimit == null ? `${ivUsed} · unlimited` : `${ivUsed} / ${ivLimit}`}
              {company.interview_credits ? <span className="text-[#0025D8]"> +{company.interview_credits} credits</span> : null}
            </span>
          </div>
          {ivLimit != null && (
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className={`h-full rounded-full ${cycleOver ? "bg-[#E11D48]" : cyclePct >= 80 ? "bg-[#F59E0B]" : "bg-[#0025D8]"}`} style={{ width: `${cyclePct}%` }} />
            </div>
          )}
          {ivLimit != null && cyclePct >= 80 && (
            <p className={`text-[11px] font-semibold mt-1 ${cycleOver ? "text-[#E11D48]" : "text-[#F59E0B]"}`}>
              {cycleOver ? "At/over plan limit — strong upgrade candidate." : "Approaching plan limit — consider a plan offer."}
            </p>
          )}
        </div>

        {/* Monthly bar chart */}
        <div className="flex items-end gap-1.5 h-28">
          {monthly_sent.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
              <span className="font-mono text-[9px] text-muted-foreground">{m.count || ""}</span>
              <div className="w-full flex items-end" style={{ height: "72px" }}>
                <div
                  className="w-full rounded-t bg-[#0025D8]/80 hover:bg-[#0025D8] transition-colors"
                  style={{ height: `${Math.round((m.count / maxMonth) * 100)}%`, minHeight: m.count > 0 ? "3px" : "0" }}
                  title={`${m.month}: ${m.count} sent`}
                />
              </div>
              <span className="font-mono text-[8px] text-muted-foreground uppercase">{monLabel(m.month)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Most common flags for this company */}
      <div className="rounded-xl border border-border bg-white p-4 mb-4">
        <FlagRanking ranking={flag_ranking} title="Most common flags" subtitle="by interviews affected" />
      </div>

      {/* Roles table */}
      <div className="rounded-xl border border-border overflow-hidden bg-white">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-display font-bold text-base">Roles & funnel</h2>
        </div>

        {roles.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">This company hasn't posted any roles yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/30 font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">
                  <th className="text-left px-4 py-2.5 font-medium">Role</th>
                  <th className="text-center px-2 py-2.5 font-medium">Applied</th>
                  <th className="text-center px-2 py-2.5 font-medium">Shortlisted</th>
                  <th className="text-center px-2 py-2.5 font-medium">Invited</th>
                  <th className="text-center px-2 py-2.5 font-medium">Completed</th>
                  <th className="text-center px-2 py-2.5 font-medium">Avg score</th>
                  <th className="text-center px-3 py-2.5 font-medium">Avg flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {roles.map((r) => (
                  <tr key={r.role_id} className="hover:bg-secondary/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0F172A] truncate max-w-[220px]">{r.title || "Untitled role"}</div>
                      <span className={`inline-block mt-1 font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded ${ROLE_STATUS_CLS[r.status] || ROLE_STATUS_CLS.draft}`}>
                        {r.status || "draft"}
                      </span>
                    </td>
                    <td className="text-center px-2 py-3 font-mono">{r.applied}</td>
                    <td className="text-center px-2 py-3 font-mono">{r.shortlisted}</td>
                    <td className="text-center px-2 py-3 font-mono">{r.invited}</td>
                    <td className="text-center px-2 py-3 font-mono">{r.completed}</td>
                    <td className="text-center px-2 py-3 font-mono font-semibold text-[#0025D8]">{fmtScore(r.avg_score)}</td>
                    <td className={`text-center px-3 py-3 font-mono font-semibold ${r.avg_flags > 0 ? "text-[#E11D48]" : "text-muted-foreground"}`}>
                      {fmtScore(r.avg_flags)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
